import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaShieldAlt, FaLock, FaKey, FaEye,
  FaNetworkWired, FaCheckCircle, FaArrowRight, FaCertificate,
  FaDatabase
} from 'react-icons/fa';
import './Security.css';

const securityPillars = [
  {
    icon: <FaShieldAlt />,
    title: 'Application Security',
    desc: 'OWASP-compliant development practices, secure code reviews, SAST/DAST scanning, and runtime application self-protection (RASP) for all fintech applications.',
    features: ['OWASP Top 10 Mitigation', 'Secure SDLC', 'Automated Code Scanning', 'Pen Testing'],
  },
  {
    icon: <FaLock />,
    title: 'Data Encryption',
    desc: 'End-to-end encryption using AES-256 for data at rest and TLS 1.3 for data in transit. Tokenization and masking of sensitive financial and PII data.',
    features: ['AES-256 Encryption', 'TLS 1.3 in Transit', 'Tokenization', 'Key Management (HSM)'],
  },
  {
    icon: <FaKey />,
    title: 'Identity & Access Management',
    desc: 'Multi-factor authentication, role-based access control, OAuth 2.0/OIDC integrations, and zero-trust architecture for all systems.',
    features: ['MFA / Biometrics', 'RBAC / ABAC', 'OAuth 2.0 & OIDC', 'Zero Trust Architecture'],
  },
  {
    icon: <FaEye />,
    title: 'Threat Detection & SOC',
    desc: 'Real-time threat monitoring with SIEM integration, anomaly detection using ML models, and 24/7 security operations center capabilities.',
    features: ['SIEM Integration', 'ML-based Anomaly Detection', '24/7 Monitoring', 'Incident Response'],
  },
  {
    icon: <FaNetworkWired />,
    title: 'Network Security',
    desc: 'Web Application Firewall (WAF), DDoS protection, intrusion detection/prevention systems, and network segmentation for financial workloads.',
    features: ['WAF & DDoS Protection', 'IDS/IPS Systems', 'Network Segmentation', 'VPN & SD-WAN'],
  },
  {
    icon: <FaDatabase />,
    title: 'Data Governance & Privacy',
    desc: 'Comprehensive data governance frameworks ensuring GDPR, PDPA, and RBI data localization compliance with audit trails and data lineage tracking.',
    features: ['GDPR Compliance', 'RBI Data Localization', 'Audit Trail Logging', 'Data Masking'],
  },
];

const certifications = [
  { name: 'PCI DSS', level: 'Level 1', desc: 'Payment Card Industry Data Security Standard for secure card processing environments.' },
  { name: 'ISO 27001', level: 'Compliant', desc: 'International standard for information security management systems.' },
  { name: 'SOC 2 Type II', level: 'Ready', desc: 'Service Organization Controls for security, availability, and confidentiality.' },
  { name: 'GDPR', level: 'Compliant', desc: 'General Data Protection Regulation for EU personal data handling.' },
  { name: 'RBI Guidelines', level: 'Compliant', desc: 'Reserve Bank of India guidelines for payment system operators and fintechs.' },
  { name: 'OWASP', level: 'Adherent', desc: 'Open Web Application Security Project top 10 vulnerability mitigations.' },
];

const securityProcess = [
  { step: '01', title: 'Security Assessment', desc: 'Thorough assessment of your existing systems to identify vulnerabilities and compliance gaps.' },
  { step: '02', title: 'Threat Modeling', desc: 'Systematic analysis of potential threats and attack vectors specific to your fintech application.' },
  { step: '03', title: 'Secure Design', desc: 'Security-first architecture with built-in controls at every layer of the technology stack.' },
  { step: '04', title: 'Implementation & Testing', desc: 'Secure coding practices, code reviews, automated scanning, and rigorous penetration testing.' },
  { step: '05', title: 'Monitoring & Response', desc: 'Continuous monitoring, threat intelligence feeds, and rapid incident response procedures.' },
  { step: '06', title: 'Compliance Audit', desc: 'Regular compliance audits, certifications maintenance, and regulatory reporting support.' },
];

function Security() {
  return (
    <div className="security-page">
      <Helmet>
        <title>Security & Compliance | Techmascot Technologies</title>
        <meta name="description" content="Enterprise-grade security solutions from Techmascot Technologies including ISO 27001 compliance, PCI-DSS standards, penetration testing and 24/7 threat monitoring." />
        <link rel="canonical" href="https://www.techmascot.com/security" />
        <meta property="og:title" content="Security & Compliance | Techmascot Technologies" />
        <meta property="og:description" content="Enterprise-grade security solutions including ISO 27001 compliance, PCI-DSS standards, penetration testing and 24/7 threat monitoring." />
        <meta property="og:url" content="https://www.techmascot.com/security" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Banner */}
      <section className="page-banner security-banner">
        <div className="banner-bg">
          <div className="banner-grid"></div>
          <div className="security-shield-bg">
            <FaShieldAlt />
          </div>
        </div>
        <div className="container">
          <div className="banner-content">
            <span className="tag">Enterprise Security</span>
            <h1>Security is Our <span>Foundation</span></h1>
            <p>
              We build fintech solutions with security embedded at every layer —
              from architecture design to deployment, monitoring, and compliance.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '32px', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary">
                Security Consultation <FaArrowRight />
              </Link>
              <Link to="/services" className="btn-secondary">
                Our Services
              </Link>
            </div>
          </div>
        </div>
        <div className="banner-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill="#FFFFFF" />
          </svg>
        </div>
      </section>

      {/* Security Stats */}
      <section className="security-stats">
        <div className="container">
          <div className="sec-stats-grid">
            {[
              { val: '256-bit', label: 'AES Encryption', icon: <FaLock /> },
              { val: 'TLS 1.3', label: 'Data in Transit', icon: <FaShieldAlt /> },
              { val: '0 Breach', label: 'Track Record', icon: <FaCertificate /> },
              { val: '24/7', label: 'Security Monitoring', icon: <FaEye /> },
            ].map((s, i) => (
              <div key={i} className="sec-stat-card">
                <div className="sec-stat-icon">{s.icon}</div>
                <div className="sec-stat-val">{s.val}</div>
                <div className="sec-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Pillars */}
      <section className="pillars-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Security Pillars</span>
            <h2 className="section-title">Multi-Layered <span>Security Framework</span></h2>
            <p className="section-subtitle">
              Our defense-in-depth approach protects financial data across all system
              layers — from application to infrastructure.
            </p>
          </div>
          <div className="pillars-grid">
            {securityPillars.map((p, i) => (
              <div key={i} className="pillar-card">
                <div className="pillar-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <ul className="pillar-features">
                  {p.features.map((f, j) => (
                    <li key={j}><FaCheckCircle /><span>{f}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="certs-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Compliance & Standards</span>
            <h2 className="section-title">Certifications & <span>Compliance</span></h2>
            <p className="section-subtitle">
              We design and implement solutions that meet the most stringent global
              financial security standards and regulatory requirements.
            </p>
          </div>
          <div className="certs-grid">
            {certifications.map((c, i) => (
              <div key={i} className="cert-card">
                <div className="cert-badge">
                  <FaCertificate />
                  <span>{c.name}</span>
                </div>
                <div className="cert-level">{c.level}</div>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Process */}
      <section className="sec-process-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Our Methodology</span>
            <h2 className="section-title">Security-First <span>Development Process</span></h2>
          </div>
          <div className="process-grid">
            {securityProcess.map((p, i) => (
              <div key={i} className="process-card">
                <div className="process-step">{p.step}</div>
                <h4>{p.title}</h4>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ind-cta">
        <div className="container">
          <div className="ind-cta-inner">
            <h2>Secure Your Fintech Platform</h2>
            <p>
              Let our security experts assess your current systems and design a
              robust security architecture for your financial applications.
            </p>
            <Link to="/contact" className="btn-primary" style={{ background: '#FFFFFF', color: '#003A87', borderColor: '#FFFFFF' }}>
              Request Security Assessment <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Security;
